// Stripe Service for UI Interaction (TypeScript, React)

import { getAuth } from "firebase/auth";
import { StripeProduct } from "./stripeTypes";
import {  getFunctions, httpsCallable } from "firebase/functions";


// for local testong only 
// import { connectFunctionsEmulator } from "firebase/functions";

// const functions = getFunctions();
// if (window.location.hostname === "localhost") {
  // connectFunctionsEmulator(functions, "localhost", 5001);
// }

const isProduction =
  process.env.REACT_APP_FORCE_PRODUCT_MODE === "true" ||
  (typeof window !== "undefined" &&
    (window.location.hostname === "peakxv2.web.app" ||
      window.location.hostname === "peak.app" ||
      window.location.hostname === "www.peak.app"));

// Premium product ID - the only product we support
const PREMIUM_PRODUCT_ID = isProduction
  ? "prod_TnfcSN7Wbkti91"
  : "prod_ToTPQZt4Xgum3P";

// Premium product features (Stripe doesn't return these via API)
const PREMIUM_FEATURES = [
  "Connect up to 3 Wearable Devices",
  "Wellness, Training Load (RPE), Niggles, Performances & Readiness logging",
  "Peak Score & Readiness Insights",
  "Heart Lung Recovery Index (HLR) tracking",
  "The Wizard AI Unlimited Access",
  "Individualised Daily Insights & AI Chat Tone",
  "Day, Week, Month, Quarter & Custom Range Insights & Analytics",
  "Priority Feature & Support Access",
  "Training Planner - Saved Sessions & Activities",
];
const PLUS_PRODUCT_ID = isProduction
  ? "prod_UA2swfTlCksMEt" // replace with actual Stripe product ID
  : "prod_UA4mYMRpZrYger";

// Add PLUS_FEATURES constant
const PLUS_FEATURES = [
  "Connect up to 1 Wearable Device",
  "Wellness, Training Load (RPE), Niggles, Performances & Readiness logging",
  "Peak Score & Readiness Insights",
  "Heart Lung Recovery Index (HLR) tracking",
  "The Wizard AI Limited Access",
  "Last 7 Days Analytics",
  "Priority Feature & Support Access",
];
// New simplified USD-only prices on the PEAK^ App Stripe account.
// Sandbox and live mode use different price IDs.
const PEAK_PLUS_PRICE_IDS_STAGING = {
  monthly: "price_1UThF2S39eCdVnEKss0xjUy3",
  yearly: "price_1UThF2S39eCdVnEKiILVWVN2",
};
const PEAK_PREMIUM_PRICE_IDS_STAGING = {
  monthly: "price_1UThHyS39eCdVnEK2wqtNhlc",
  yearly: "price_1UThHyS39eCdVnEKML9NZi5J",
};
const PEAK_PLUS_PRICE_IDS_LIVE = {
  monthly: "price_1UVJ5SS8gobt3IzcMFAsFTV7",
  yearly: "price_1UVJ5SS8gobt3IzcCCey6Bg0",
};
const PEAK_PREMIUM_PRICE_IDS_LIVE = {
  monthly: "price_1UVJ5LS8gobt3Izc0lK1M2s4",
  yearly: "price_1UVJ5LS8gobt3IzcLD8VKMd6",
};
const PEAK_PLUS_PRICE_IDS = isProduction ? PEAK_PLUS_PRICE_IDS_LIVE : PEAK_PLUS_PRICE_IDS_STAGING;
const PEAK_PREMIUM_PRICE_IDS = isProduction ? PEAK_PREMIUM_PRICE_IDS_LIVE : PEAK_PREMIUM_PRICE_IDS_STAGING;
const PEAK_PLUS_PRICE_IDS_ALL = [
  ...Object.values(PEAK_PLUS_PRICE_IDS_STAGING),
  ...Object.values(PEAK_PLUS_PRICE_IDS_LIVE),
];
const PEAK_PREMIUM_PRICE_IDS_ALL = [
  ...Object.values(PEAK_PREMIUM_PRICE_IDS_STAGING),
  ...Object.values(PEAK_PREMIUM_PRICE_IDS_LIVE),
];

const getPeakPriceId = (
  plan: "plus" | "premium",
  billingPeriod: "month" | "year",
): string => {
  const ids = plan === "plus" ? PEAK_PLUS_PRICE_IDS : PEAK_PREMIUM_PRICE_IDS;
  return billingPeriod === "year" ? ids.yearly : ids.monthly;
};

const isPeakPrice = (priceId: string | null | undefined): boolean =>
  !!priceId &&
  (PEAK_PLUS_PRICE_IDS_ALL.includes(priceId) ||
    PEAK_PREMIUM_PRICE_IDS_ALL.includes(priceId));

const getBillingPeriodForPeakPriceId = (
  priceId: string | null | undefined,
): "month" | "year" | null => {
  if (!priceId) return null;
  if (
    priceId === PEAK_PLUS_PRICE_IDS_STAGING.monthly ||
    priceId === PEAK_PLUS_PRICE_IDS_LIVE.monthly ||
    priceId === PEAK_PREMIUM_PRICE_IDS_STAGING.monthly ||
    priceId === PEAK_PREMIUM_PRICE_IDS_LIVE.monthly
  ) {
    return "month";
  }
  if (
    priceId === PEAK_PLUS_PRICE_IDS_STAGING.yearly ||
    priceId === PEAK_PLUS_PRICE_IDS_LIVE.yearly ||
    priceId === PEAK_PREMIUM_PRICE_IDS_STAGING.yearly ||
    priceId === PEAK_PREMIUM_PRICE_IDS_LIVE.yearly
  ) {
    return "year";
  }
  return null;
};

// Flat USD display prices — no live Stripe fetch needed for these, same
// numbers for every user.
const PEAK_DISPLAY_PRICES = {
  plus: { monthly: 9.99, yearly: 99.99 },
  premium: { monthly: 17.99, yearly: 179.99 },
};

// Country/Region to Price ID mapping
// Each region has its own price ID for each billing period
type PriceRegion = "US" | "CA" | "AU" | "ZA" | "EU" | "GB" | "IN";

interface RegionPriceIds {
  quarterly: string;
  yearly: string;
  monthly: string;
}

// Production price IDs by region
const PRODUCTION_PRICE_IDS: Record<PriceRegion, RegionPriceIds> = {
  US: {
    quarterly: "price_1Sq4DCLobYZosgniMcKRuapS",
    yearly: "price_1Sq4DrLobYZosgniUYxTO2Wt",
    monthly: "price_1T6Se5LobYZosgni0qsAibIa",
  },
  CA: {
    quarterly: "price_1SpyqTLobYZosgniNzHnXzU5",
    yearly: "price_1SpysMLobYZosgniIZqBn0jj",
    monthly: "price_1T6SfkLobYZosgnivbdsks1K",
  },
  AU: {
    quarterly: "price_1Sq48xLobYZosgni0oih0aQH",
    yearly: "price_1Sq49JLobYZosgniJSKmaBhI",
    monthly: "price_1T6PzHLobYZosgnixBzBVc1Y",
  },
  ZA: {
    quarterly: "price_1Sq4BFLobYZosgnizRMdPGS0",
    yearly: "price_1Sq4BoLobYZosgniV0NZgkNm",
    monthly: "price_1T6SgHLobYZosgniLy5BeluT",
  },
  EU: {
    quarterly: "price_1Sq4FFLobYZosgniPBYFWazc",
    yearly: "price_1Sq4FjLobYZosgni8rWNmTvy",
    monthly: "price_1T6SeULobYZosgniMhcq6P6I",
  },
  GB: {
    quarterly: "price_1Sq4GpLobYZosgniXFSIkamr",
    yearly: "price_1Sq4HKLobYZosgniNMh6rINQ",
    monthly: "price_1T6SfELobYZosgniynftoB4P",
  },
  IN: {
    quarterly: "price_1Sq4I9LobYZosgninwocefyq",
    yearly: "price_1Sq4IbLobYZosgniMDY655sa",
    monthly: "price_1T6SgfLobYZosgniwOKxkPiu",
  },
};

// Staging price IDs by region (placeholders - update with actual staging IDs)
const STAGING_PRICE_IDS: Record<PriceRegion, RegionPriceIds> = {
  US: {
    quarterly: "price_1TH3ipPuUOai2KifuScl5KGQ",
    yearly: "price_1SqqSMPuUOai2Kifeeo15aQf",
    monthly: "price_1T6w9RPuUOai2KifAzTz40cB",
  },
  CA: {
    quarterly: "price_1SqqdtPuUOai2Kif3inmjvcu",
    yearly: "price_1SqqcwPuUOai2KifauHmazVl",
    monthly: "price_1T6w5mPuUOai2Kif0FfLrUjy",
  },
  AU: {
    quarterly: "price_1SqqW5PuUOai2Kif5cvVfPwL",
    yearly: "price_1SqqX8PuUOai2KifxAAeAui9",
    monthly: "price_1T6w7EPuUOai2KifQ0jtzEbl",
  },
  ZA: {
    quarterly: "price_1SqqV7PuUOai2KifyXz5e1iA",
    yearly: "price_1SqqU9PuUOai2KifmsLnzLoG",
    monthly: "price_1T6w87PuUOai2Kif2ITnoKvb",
  },
  EU: {
    quarterly: "price_1SqqQmPuUOai2Kifoe4eSVOP",
    yearly: "price_1SqqPxPuUOai2KifnSZad9O2",
    monthly: "price_1T6wA9PuUOai2KifLpEhgbOi",
  },
  GB: {
    quarterly: "price_1SqqO7PuUOai2KifOPoo1tQk",
    yearly: "price_1SqqNPPuUOai2KifH6HUqa4q",
    monthly: "price_1T6wAxPuUOai2Kifj0xPegYm",
  },
  IN: {
    quarterly: "price_1SqqMPPuUOai2KifHKqKtfjr",
    yearly: "price_1SqqLlPuUOai2KifcDPMau0D",
    monthly: "price_1T6wByPuUOai2Kifslx3wFTK",
  },
};
const PLUS_PRODUCTION_PRICE_IDS: Record<PriceRegion, RegionPriceIds> = {
  US: {
    monthly: "price_1TBimyLobYZosgniLbL6p20j",
    yearly: "price_1TBincLobYZosgniJAZVrmpk",
    quarterly: "price_1TBincLobYZosgnipyhFGkCH",
  },
  CA: {
    monthly: "price_1TBiqmLobYZosgnia9KX0P06",
    yearly: "price_1TBirJLobYZosgnikKrcFEiB",
    quarterly: "price_1TBir8LobYZosgniNFzlzLHH",
  },
  AU: {
    monthly: "price_1TBilXLobYZosgniisxkWQWp",
    yearly: "price_1TBilXLobYZosgninD35CK9y",
    quarterly: "price_1TBilXLobYZosgniL1AIGH2b",
  },
  ZA: {
    monthly: "price_1TBiucLobYZosgniIdMtj4gt",
    yearly: "price_1TBivILobYZosgnirFB0VVYe",
    quarterly: "price_1TBiuzLobYZosgniak98Eda8",
  },
  EU: {
    monthly: "price_1TBio5LobYZosgni2Iv2KH2A",
    yearly: "price_1TBiocLobYZosgnidXMw4jMv",
    quarterly: "price_1TBioQLobYZosgniSmkv0Vvk",
  },
  GB: {
    monthly: "price_1TBipDLobYZosgnil9o4ovAw",
    yearly: "price_1TBipwLobYZosgniIytHbPft",
    quarterly: "price_1TBipdLobYZosgnixqu1WewH",
  },
  IN: {
    monthly: "price_1TBiscLobYZosgnijQodhWBa",
    yearly: "price_1TBitSLobYZosgnik2tnobZ6",
    quarterly: "price_1TBiszLobYZosgniWbkgubYY",
  },
};

const PLUS_STAGING_PRICE_IDS: Record<PriceRegion, RegionPriceIds> = {
  US: {
    monthly: "price_1TBkcYPuUOai2KifatSOx1pl",
    yearly: "price_1TBkcYPuUOai2KifLgdEW49C",
    quarterly: "price_1TBkcYPuUOai2KifkIThsmYz",
  },
  CA: {
    monthly: "price_1TBkUpPuUOai2KifNGNEoKtS",
    yearly: "price_1TBkUpPuUOai2KifoA42AxwU",
    quarterly: "price_1TBkUpPuUOai2KiffsfSCbmO",
  },
  AU: {
    monthly: "price_1TBkQWPuUOai2KifgcaCNHVN",
    yearly: "price_1TBkQWPuUOai2KifEeGod9hi",
    quarterly: "price_1TBkQWPuUOai2KifqRuTrBvW",
  },
  ZA: {
    monthly: "price_1TBkSzPuUOai2Kif7FldRazK",
    yearly: "price_1TBkSzPuUOai2KifIntryFTQ",
    quarterly: "price_1TBkT0PuUOai2Kif3dSfLvnE",
  },
  EU: {
    monthly: "price_1TBkadPuUOai2Kify69pkyac",
    yearly: "price_1TBkadPuUOai2KifFtkRxkl2",
    quarterly: "price_1TBkadPuUOai2Kif9X676qXv",
  },
  GB: {
    monthly: "price_1TBkYKPuUOai2KifbAc67NzR",
    yearly: "price_1TBkYKPuUOai2Kif3k5KBHsp",
    quarterly: "price_1TBkYKPuUOai2Kif9V7ajXor",
  },
  IN: {
    monthly: "price_1TBkelPuUOai2KiflqDZjFiN",
    yearly: "price_1TBkelPuUOai2KifhTej8IU1",
    quarterly: "price_1TBkelPuUOai2KifYHr4WdKZ",
  },
};

const PLUS_PRICE_IDS_BY_REGION = isProduction
  ? PLUS_PRODUCTION_PRICE_IDS
  : PLUS_STAGING_PRICE_IDS;
const PRICE_IDS_BY_REGION = isProduction
  ? PRODUCTION_PRICE_IDS
  : STAGING_PRICE_IDS;

// Map country names to price regions
// Countries not listed will default to US pricing
const COUNTRY_TO_REGION: Record<string, PriceRegion> = {
  // United States
  "United States": "US",
  // Canada
  Canada: "CA",
  // Australia & New Zealand
  Australia: "AU",
  "New Zealand": "AU",
  // South Africa
  "South Africa": "ZA",
  // Europe (EUR countries)
  Austria: "EU",
  Belgium: "EU",
  Cyprus: "EU",
  Estonia: "EU",
  Finland: "EU",
  France: "EU",
  Germany: "EU",
  Greece: "EU",
  Ireland: "EU",
  Italy: "EU",
  Latvia: "EU",
  Lithuania: "EU",
  Luxembourg: "EU",
  Malta: "EU",
  Netherlands: "EU",
  Portugal: "EU",
  Slovakia: "EU",
  Slovenia: "EU",
  Spain: "EU",
  // United Kingdom
  "United Kingdom": "GB",
  // India
  India: "IN",
};
const getFallbackProducts = (): StripeProduct[] => {
  const base = isProduction
    ? [
        {
          productId: "prod_UA2swfTlCksMEt",
          name: "PEAK Athlete Plus (USD)",
          plans: [
            {
              id: "price_1TBincLobYZosgnipyhFGkCH",
              currency: "USD",
              duration: "month" as const,
              price: 10.99,
            },
            {
              id: "price_1TBincLobYZosgniJAZVrmpk",
              currency: "USD",
              duration: "year" as const,
              price: 32.99,
            },
          ],
          type: "plus",
          features: PLUS_FEATURES,
          comingSoon: false,
          isStripe: true,
        },
        {
          productId: "prod_TnfcSN7Wbkti91",
          name: "PEAK Athlete Premium (GBP)",
          plans: [
            {
              id: "price_1T6SfELobYZosgniynftoB4P",
              currency: "GBP",
              duration: "month" as const,
              price: 13.99,
            },
            {
              id: "price_1Sq4HKLobYZosgniNMh6rINQ",
              currency: "GBP",
              duration: "year" as const,
              price: 84.99,
            },
          ],
          type: "premium",
          features: PREMIUM_FEATURES,
          comingSoon: false,
          isStripe: true,
        },
      ]
    : [
        {
          productId: "prod_UA4mYMRpZrYger",
          name: "PEAK Athlete Plus (USD)",
          plans: [
            {
              id: "price_1TBkcYPuUOai2KifLgdEW49C",
              currency: "USD",
              duration: "month" as const,
              price: 32.99,
            },
            {
              id: "price_1TBkcYPuUOai2KifkIThsmYz",
              currency: "USD",
              duration: "year" as const,
              price: 10.99,
            },
          ],
          type: "plus",
          features: PLUS_FEATURES,
          comingSoon: false,
          isStripe: true,
        },
        {
          productId: "prod_ToTPQZt4Xgum3P",
          name: "PEAK Athlete Premium (USA)",
          plans: [
            {
              id: "price_1T6w9RPuUOai2KifAzTz40cB",
              currency: "USD",
              duration: "month" as const,
              price: 13.99,
            },
            {
              id: "price_1SqqSMPuUOai2Kifeeo15aQf",
              currency: "USD",
              duration: "year" as const,
              price: 109.99,
            },
          ],
          type: "premium",
          features: PREMIUM_FEATURES,
          comingSoon: false,
          isStripe: true,
        },
      ];
  const results: StripeProduct[] = [];

  // ── Premium product ──────────────────────────────────────────────────
  const premiumMatches = base
    .filter((p) => p.productId === PREMIUM_PRODUCT_ID)
    .map((p) => ({
      ...p,
      plans: (p.plans || []).map((plan: any) => ({
        ...plan,
        duration: normalizeDuration(plan.duration, plan.intervalCount),
      })),
      isStripe: true,
      comingSoon: false,
      type: "premium", // ← explicit type tag
      features: PREMIUM_FEATURES,
    }));

  results.push(...premiumMatches);

  // ── Plus product ─────────────────────────────────────────────────────
  const plusMatches = base
    .filter((p) => p.productId === PLUS_PRODUCT_ID)
    .map((p) => ({
      ...p,
      plans: (p.plans || []).map((plan: any) => ({
        ...plan,
        duration: normalizeDuration(plan.duration, plan.intervalCount),
      })),
      isStripe: true,
      comingSoon: false,
      type: "plus", // ← explicit type tag
      features: PLUS_FEATURES,
    }));

  results.push(...plusMatches);

  return base;
};
// ==========================================
// IP-based Country Validation
// ==========================================

// Cache for IP-based country detection
let cachedIpCountry: string | null = null;
let ipCountryFetchPromise: Promise<string | null> | null = null;

/**
 * Detect user's country from their IP address using a free geolocation API
 * Results are cached for the session to avoid repeated API calls
 */
const detectCountryFromIp = async (): Promise<string | null> => {
  // Return cached result if available
  if (cachedIpCountry !== null) {
    return cachedIpCountry;
  }

  // If a fetch is already in progress, wait for it
  if (ipCountryFetchPromise) {
    return ipCountryFetchPromise;
  }

  // Start the fetch
  ipCountryFetchPromise = (async () => {
    try {
      // Using ipapi.co - free tier allows 1000 requests/day
      const response = await fetch("https://ipapi.co/json/", {
        method: "GET",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        console.warn(
          "[IP_DETECTION] Failed to fetch IP location:",
          response.status,
        );
        return null;
      }

      const data = await response.json();
      const country = data.country_name || null;

      console.log("[IP_DETECTION] Detected country from IP:", country);
      cachedIpCountry = country;
      return country;
    } catch (error) {
      console.warn("[IP_DETECTION] Error detecting country from IP:", error);
      return null;
    } finally {
      ipCountryFetchPromise = null;
    }
  })();

  return ipCountryFetchPromise;
};

/**
 * Validate if the user's profile country matches their IP-based country
 * Returns the validated country to use for pricing, or "United States" if mismatch
 */
const validateCountryForPricing = async (
  profileCountry: string | null | undefined,
): Promise<string> => {
  // If no profile country set, use US pricing
  if (!profileCountry) {
    // console.log(
    //   "[COUNTRY_VALIDATION] No profile country set, using US pricing",
    // );
    return "United States";
  }

  const ipCountry = await detectCountryFromIp();

  // If we couldn't detect IP country, trust the profile (fail open)
  if (!ipCountry) {
    // console.log(
    //   "[COUNTRY_VALIDATION] Could not detect IP country, trusting profile:",
    //   profileCountry,
    // );
    return profileCountry;
  }

  // Check if profile country matches IP country
  // We do a case-insensitive comparison and also check if they're in the same region
  const profileRegion = COUNTRY_TO_REGION[profileCountry] || "US";
  const ipRegion = COUNTRY_TO_REGION[ipCountry] || "US";

  if (
    profileCountry.toLowerCase() === ipCountry.toLowerCase() ||
    profileRegion === ipRegion
  ) {
    // console.log("[COUNTRY_VALIDATION] Country validated:", profileCountry);
    return profileCountry;
  }

  // Mismatch detected - use US pricing as fallback
  // console.warn(
  //   "[COUNTRY_VALIDATION] Country mismatch detected!",
  //   { profileCountry, ipCountry, profileRegion, ipRegion },
  //   "Using US pricing",
  // );
  return "United States";
};

/**
 * Get the cached IP country (synchronous) - returns null if not yet fetched
 */
const getCachedIpCountry = (): string | null => {
  return cachedIpCountry;
};

// Fallback display prices by region (used when API doesn't return the new price IDs)
// These should match the actual Stripe prices - update when prices change
interface FallbackPrice {
  quarterly: { price: number; currency: string };
  yearly: { price: number; currency: string };
  monthly: { price: number; currency: string };
}
const PLUS_FALLBACK_DISPLAY_PRICES: Record<PriceRegion, FallbackPrice> = {
  US: {
    monthly: { price: 3.99, currency: "USD" },
    quarterly: { price: 10.99, currency: "USD" },
    yearly: { price: 32.99, currency: "USD" },
  },
  CA: {
    monthly: { price: 5.99, currency: "CAD" },
    quarterly: { price: 12.99, currency: "CAD" },
    yearly: { price: 38.99, currency: "CAD" },
  },
  AU: {
    monthly: { price: 5.99, currency: "AUD" },
    quarterly: { price: 12.99, currency: "AUD" },
    yearly: { price: 38.99, currency: "AUD" },
  },
  ZA: {
    monthly: { price: 38.75, currency: "ZAR" },
    quarterly: { price: 137.99, currency: "ZAR" },
    yearly: { price: 419.99, currency: "ZAR" },
  },
  EU: {
    monthly: { price: 4.99, currency: "EUR" },
    quarterly: { price: 9.99, currency: "EUR" },
    yearly: { price: 29.99, currency: "EUR" },
  },
  GB: {
    monthly: { price: 3.49, currency: "GBP" },
    quarterly: { price: 9.99, currency: "GBP" },
    yearly: { price: 29.99, currency: "GBP" },
  },
  IN: {
    monthly: { price: 270, currency: "INR" },
    quarterly: { price: 599, currency: "INR" },
    yearly: { price: 1899, currency: "INR" },
  },
};
const FALLBACK_DISPLAY_PRICES: Record<PriceRegion, FallbackPrice> = {
  US: {
    quarterly: { price: 34.99, currency: "USD" },
    yearly: { price: 109.99, currency: "USD" },
    monthly: { price: 16.99, currency: "USD" },
  },
  CA: {
    quarterly: { price: 41.99, currency: "CAD" },
    yearly: { price: 129.99, currency: "CAD" },
    monthly: { price: 19.99, currency: "CAD" },
  },
  AU: {
    quarterly: { price: 41.99, currency: "AUD" },
    yearly: { price: 129.99, currency: "AUD" },
    monthly: { price: 19.99, currency: "AUD" },
  },
  ZA: {
    quarterly: { price: 459.99, currency: "ZAR" },
    yearly: { price: 1399, currency: "ZAR" },
    monthly: { price: 219.99, currency: "ZAR" },
  },
  EU: {
    quarterly: { price: 32.99, currency: "EUR" },
    yearly: { price: 99.99, currency: "EUR" },
    monthly: { price: 15.99, currency: "EUR" },
  },
  GB: {
    quarterly: { price: 27.99, currency: "GBP" },
    yearly: { price: 84.99, currency: "GBP" },
    monthly: { price: 13.99, currency: "GBP" },
  },
  IN: {
    quarterly: { price: 1999, currency: "INR" },
    yearly: { price: 6249, currency: "INR" },
    monthly: { price: 949, currency: "INR" },
  },
};

// Helper to normalize Stripe's duration quirks
// Stripe returns "month" for quarterly plans (with interval_count: 3)
// We normalize to "quarter" for consistency
const normalizeDuration = (
  duration: string,
  intervalCount?: number,
): string => {
  if (duration === "month" && intervalCount === 3) {
    return "quarter";
  }
  return duration;
};

const API_BASE_SANDBOX = {
  products: "https://getstripeproducts-kcepz7nnjq-uc.a.run.app",
  createCustomer: "https://createstripecustomer-kcepz7nnjq-uc.a.run.app",
  checkout: "https://createcheckoutsession-kcepz7nnjq-uc.a.run.app",
  cancel: "https://cancelsubscription-kcepz7nnjq-uc.a.run.app",
  resume: "https://resumesubscription-kcepz7nnjq-uc.a.run.app",
  update: "https://updatesubscription-kcepz7nnjq-uc.a.run.app",
  cancelAtEnd: "https://cancelatperiodendsubscription-kcepz7nnjq-uc.a.run.app",
  validate: "https://validatepromocode-kcepz7nnjq-uc.a.run.app",
  portalSession: "https://createstripeportalsession-kcepz7nnjq-uc.a.run.app",

};

const API_BASE_PRODUCTION = {
  products: "https://getstripeproducts-kp6dswkmca-uc.a.run.app",
  createCustomer: "https://createstripecustomer-kp6dswkmca-uc.a.run.app",
  checkout: "https://createcheckoutsession-kp6dswkmca-uc.a.run.app",
  cancel: "https://cancelsubscription-kp6dswkmca-uc.a.run.app",
  resume: "https://resumesubscription-kp6dswkmca-uc.a.run.app",
  update: "https://updatesubscription-kp6dswkmca-uc.a.run.app",
  cancelAtEnd: "https://cancelatperiodendsubscription-kp6dswkmca-uc.a.run.app",
  validate: "https://validatepromocode-kp6dswkmca-uc.a.run.app",
  portalSession: "https://createstripeportalsession-kp6dswkmca-uc.a.run.app",

};

const API_BASE = isProduction ? API_BASE_PRODUCTION : API_BASE_SANDBOX;

interface ValidatePromoResult {
  valid: boolean;
  promoId?: string;
  discountAmount?: number;
  type?: "amount" | "percent";
  message?: string;
}

// Helper to include Firebase ID token in headers
const authHeader = async () => {
  const currentUser = getAuth().currentUser;
  if (!currentUser) throw new Error("User not authenticated");
  const token = await currentUser.getIdToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

/**
 * Get the price region for a given country
 * Returns "US" as default if country is not mapped
 */
const getRegionForCountry = (
  country: string | null | undefined,
): PriceRegion => {
  if (!country) return "US";
  return COUNTRY_TO_REGION[country] || "US";
};

/**
 * Get the Stripe price ID for a given country and billing period
 * Falls back to US pricing if country is not mapped
 */
const getPriceIdForCountry = (
  country: string | null | undefined,
  billingPeriod: "free" | "month" | "quarter" | "year",
): string => {
  const region = getRegionForCountry(country);
  const regionPrices = PRICE_IDS_BY_REGION[region];
  // return billingPeriod === "year" ? regionPrices.yearly : regionPrices.quarterly;
  if (billingPeriod === "year") return regionPrices.yearly;
  if (billingPeriod === "month") return regionPrices.monthly;
  return regionPrices.quarterly;
};

/**
 * Get all price IDs for a region (both quarterly and yearly)
 */
const getPriceIdsForCountry = (
  country: string | null | undefined,
): RegionPriceIds => {
  const region = getRegionForCountry(country);
  return PRICE_IDS_BY_REGION[region];
};

// Legacy price IDs (old multi-currency prices) - for detecting current subscriptions
const LEGACY_PRICE_IDS = {
  production: {
    quarterly: "price_1Sm0jOLobYZosgniDp0fU6C0",
    yearly: "price_1Sm0otLobYZosgniMueUguAB",
  },
  staging: {
    quarterly: "price_1Sn7kTPuUOai2KifjDn4Eb3K",
    yearly: "price_1SnAgAPuUOai2Kifnc9ffnJb",
  },
};

/**
 * Get the billing period for a price ID
 * Works with both new region-specific and legacy price IDs
 */
const getBillingPeriodForPriceId = (
  priceId: string | null | undefined,
): "quarter" | "year" | null => {
  if (!priceId) return null;

  // Check all regions for the new price IDs
  for (const region of Object.keys(PRICE_IDS_BY_REGION) as PriceRegion[]) {
    const regionPrices = PRICE_IDS_BY_REGION[region];
    if (regionPrices.quarterly === priceId) return "quarter";
    if (regionPrices.yearly === priceId) return "year";
  }

  // Check legacy price IDs
  const legacyPrices = isProduction
    ? LEGACY_PRICE_IDS.production
    : LEGACY_PRICE_IDS.staging;
  if (legacyPrices.quarterly === priceId) return "quarter";
  if (legacyPrices.yearly === priceId) return "year";

  return null;
};

/**
 * Get the price details for a country from the plans array
 * Looks up the price ID for the country and finds the matching plan
 * Falls back to static display prices if API doesn't return the new prices
 */
const getPriceDetailsForCountry = (
  plans: Array<{
    id: string;
    price: number;
    currency: string;
    duration: string;
  }>,
  country: string | null | undefined,
  billingPeriod: "free" | "month" | "quarter" | "year",
): { price: number; currency: string; duration: string; priceId: string } => {
  const region = getRegionForCountry(country);
  const priceId = getPriceIdForCountry(country, billingPeriod);
  const plan = plans.find((p) => p.id === priceId);

  // If found in API response, use the API price
  if (plan) {
    return {
      price: plan.price,
      currency: plan.currency.toUpperCase(),
      duration: normalizeDuration(plan.duration),
      priceId: plan.id,
    };
  }

  // ❌ REMOVE THIS SECTION - it causes the US pricing to show
  // const usPriceId = getPriceIdForCountry("United States", billingPeriod);
  // const usPlan = plans.find((p) => p.id === usPriceId);
  // if (usPlan) {
  //   return {
  //     price: usPlan.price,
  //     currency: usPlan.currency.toUpperCase(),
  //     duration: normalizeDuration(usPlan.duration),
  //     priceId: usPlan.id,
  //   };
  // }

  // Final fallback: use static display prices with correct regional price ID
  const fallbackPrices = FALLBACK_DISPLAY_PRICES[region];
  const fallback =
    billingPeriod === "year"
      ? fallbackPrices.yearly
      : billingPeriod === "month"
        ? fallbackPrices.monthly
        : fallbackPrices.quarterly;

  return {
    price: fallback.price,
    currency: fallback.currency,
    duration: billingPeriod,
    priceId: priceId, // ← Use the correct India price ID, not US
  };
};
export const StripeService = {
  /** Get the Premium product ID for the current environment */
  getPremiumProductId: () => PREMIUM_PRODUCT_ID,

  /** Get the Premium features list */
  getPremiumFeatures: () => PREMIUM_FEATURES,

  getPlusProductId: () => PLUS_PRODUCT_ID,
  getPlusFeatures: () => PLUS_FEATURES,

  /** New simplified USD-only pricing (PEAK account) — flat for every user, no region lookup. */
  getPeakPriceId,
  isPeakPrice,
  getBillingPeriodForPeakPriceId,
  getPeakDisplayPrice: (plan: "plus" | "premium", billingPeriod: "month" | "year") =>
    PEAK_DISPLAY_PRICES[plan][billingPeriod === "year" ? "yearly" : "monthly"],

  getPlusPriceIdForCountry: (
    country: string | null | undefined,
    billingPeriod: "free" | "month" | "quarter" | "year",
  ): string => {
    const region = getRegionForCountry(country);
    const regionPrices = PLUS_PRICE_IDS_BY_REGION[region];
    if (billingPeriod === "year") return regionPrices.yearly;
    if (billingPeriod === "month") return regionPrices.monthly;
    return regionPrices.quarterly;
  },
  getPlusPriceDetailsForCountry: (
    plans: Array<{
      id: string;
      price: number;
      currency: string;
      duration: string;
    }>,
    country: string | null | undefined,
    billingPeriod: "free" | "month" | "quarter" | "year",
  ): { price: number; currency: string; duration: string; priceId: string } => {
    const region = getRegionForCountry(country);
    const priceId = StripeService.getPlusPriceIdForCountry(
      country,
      billingPeriod,
    );
    const plan = plans.find((p) => p.id === priceId);

    if (plan) {
      return {
        price: plan.price,
        currency: plan.currency.toUpperCase(),
        duration: normalizeDuration(plan.duration),
        priceId: plan.id,
      };
    }

    const fallbackPrices = PLUS_FALLBACK_DISPLAY_PRICES[region];
    const fallback =
      billingPeriod === "year"
        ? fallbackPrices.yearly
        : billingPeriod === "month"
          ? fallbackPrices.monthly
          : fallbackPrices.quarterly;

    return {
      price: fallback.price,
      currency: fallback.currency,
      duration: billingPeriod,
      priceId,
    };
  },

  /** Get the price region for a country (US, CA, AU, ZA, EU, GB, IN) */
  getRegionForCountry,

  /** Get the Stripe price ID based on user's country and billing period */
  getPriceIdForCountry,

  /** Get all price IDs (quarterly and yearly) for a country */
  getPriceIdsForCountry,

  /** Get billing period from a price ID (works with legacy and new price IDs) */
  getBillingPeriodForPriceId,

  /** Get price details (price, currency, duration) for a country from API plans */
  getPriceDetailsForCountry,

  /** Detect country from IP address (async, cached) */
  detectCountryFromIp,

  /** Validate profile country against IP country - returns validated country for pricing */
  validateCountryForPricing,

  /** Get cached IP country (synchronous) - returns null if not yet fetched */
  getCachedIpCountry,

  /** Normalize duration from Stripe's format to our format
   * Stripe returns "month" for quarterly plans, we normalize to "quarter"
   */
  normalizeDuration,

  /** Fetch Stripe products - returns Premium product with API price IDs
   * Prices are fetched directly from Stripe API, not hardcoded
   */

  getProducts: async (): Promise<StripeProduct[]> => {
    try {
      const headers = await authHeader();
      const res = await fetch(API_BASE.products, {
        method: "GET",
        headers,
      });

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const stripeProducts = (await res.json()) as StripeProduct[];
      // console.log("response from stripe api", stripeProducts);

      const results: StripeProduct[] = [];

      // ── Premium product ──────────────────────────────────────────────────
      const premiumMatches = stripeProducts
        .filter((p) => p.productId === PREMIUM_PRODUCT_ID)
        .map((p) => ({
          ...p,
          plans: (p.plans || []).map((plan: any) => ({
            ...plan,
            duration: normalizeDuration(plan.duration, plan.intervalCount),
          })),
          isStripe: true,
          comingSoon: false,
          type: "premium", // ← explicit type tag
          features: PREMIUM_FEATURES,
        }));

      results.push(...premiumMatches);

      // ── Plus product ─────────────────────────────────────────────────────
      const plusMatches = stripeProducts
        .filter((p) => p.productId === PLUS_PRODUCT_ID)
        .map((p) => ({
          ...p,
          plans: (p.plans || []).map((plan: any) => ({
            ...plan,
            duration: normalizeDuration(plan.duration, plan.intervalCount),
          })),
          isStripe: true,
          comingSoon: false,
          type: "plus", // ← explicit type tag
          features: PLUS_FEATURES,
        }));

      results.push(...plusMatches);

      if (results.length === 0) {
        // console.error("[STRIPE_SERVICE] No products found from Stripe API!");
        return getFallbackProducts();
      }

      // console.log("[STRIPE_SERVICE] Products with API pricing:", results);
      return results;
    } catch (err) {
      console.error("Error fetching products from Stripe:", err);
      return getFallbackProducts();
    }
  },

  /** Create a Stripe customer mapped to Firebase user */
  createCustomer: async (
    uid: string,
    email: string,
  ): Promise<string | null> => {
    try {
      const headers = await authHeader();
      const res = await fetch(API_BASE.createCustomer, {
        method: "POST",
        headers,
        body: JSON.stringify({ uid, email }),
      });

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();

      return data.customerId ?? null;
    } catch (err) {
      console.error("Error creating Stripe customer:", err);
      return null;
    }
  },

  /** Create a Stripe Checkout Session on the new PEAK account. */
  createCheckoutSession: async (
    priceId: string,
    customerId: string,
    successUrl: string,
    cancelUrl: string,
    uid: string,
    name: string,
    trialDays?: number,
    switchFromSubscriptionId?: string,
    source?: "onboarding",
  ): Promise<string | null> => {
    if (!isPeakPrice(priceId)) {
      console.error("Refusing checkout for a price outside the new PEAK account", priceId);
      return null;
    }
    try {
      const headers = await authHeader();
      const res = await fetch(API_BASE.checkout, {
        method: "POST",
        headers,
        body: JSON.stringify({
          priceId,
          customerId,
          successUrl,
          cancelUrl,
          uid,
          name,
          code: "",
          trialDays: trialDays ?? 0,
          ...(switchFromSubscriptionId ? { switchFromSubscriptionId } : {}),
          ...(source ? { source } : {}),
        }),
      });

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();

      return data.url ?? null;
    } catch (err) {
      console.error("Error creating checkout session:", err);
      return null;
    }
  },



  /** Update (upgrade/downgrade) subscription */
updateSubscription: async (
  subscriptionId: string,
  newPriceId: string,
  isTrialing: boolean = false,
): Promise<boolean> => {
  try {
    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    const headers = await authHeader();
    const res = await fetch(API_BASE.update, {
      method: "POST",
      headers,
      body: JSON.stringify({ subscriptionId, newPriceId, isTrialing, uid }),
    });

    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    await res.json();
    return true;
  } catch (err) {
    console.error("Error updating subscription:", err);
    return false;
  }
},
    /** Cancel an active subscription */
  cancelSubscription: async (
    subscriptionId: string,
    uid: string | null,
    feedback?: any // ADD THIS
  ): Promise<boolean> => {
    try {
      const headers = await authHeader();
      const res = await fetch(API_BASE.cancel, {
        method: "POST",
        headers,
        // APPEND FEEDBACK TO BODY
        body: JSON.stringify({ subscriptionId, uid, feedback }), 
      });

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      await res.json();
      return true;
    } catch (err) {
      console.error("Error canceling subscription:", err);
      return false;
    }
  },

  cancelAtPeriodEnd: async (
    subscriptionId: string,
    uid: string | null,
    feedback?: any // ADD THIS
  ): Promise<boolean> => {
    try {
      const headers = await authHeader();
      const res = await fetch(API_BASE.cancelAtEnd, {
        method: "POST",
        headers,
        // APPEND FEEDBACK TO BODY
        body: JSON.stringify({ subscriptionId, uid, feedback }), 
      });

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      await res.json();
      return false;
    } catch (err) {
      console.error("Error canceling subscription at period end:", err);
      return false;
    }
  },

  resumeSubscription: async (
    subscriptionId: string,
    uid: string | null,
  ): Promise<boolean> => {
    try {
      const headers = await authHeader();
      const res = await fetch(API_BASE.resume, {
        method: "POST",
        headers,
        body: JSON.stringify({ subscriptionId, uid }),
      });

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      await res.json();
      return true;
    } catch (err) {
      console.error("Error resuming subscription:", err);
      return false;
    }
  },

  validatePromoCode: async (
    code: string,
    priceId: string,
  ): Promise<ValidatePromoResult> => {
    try {
      const headers = await authHeader();
      const res = await fetch(API_BASE.validate, {
        method: "POST",
        headers,
        body: JSON.stringify({ code, priceId }),
      });

      if (!res.ok) {
        const text = await res.text();
        console.warn("Promo code validation HTTP error:", res.status, text);
        return { valid: false, message: "Failed to validate promo code" };
      }

      const data = await res.json();
      return data;
    } catch (err) {
      console.error("Error calling validatePromoCode:", err);
      return { valid: false, message: "Error validating promo code" };
    }
  },
  markSubscriptionMessageRead: async (): Promise<boolean> => {
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, "markSubscriptionMessageRead");

      await callable(); // UID comes from auth token, not payload

      return true;
    } catch (err) {
      console.error("Error marking subscription message read:", err);
      return false;
    }
  },
//     createPortalSession: async (uid: string, returnUrl: string): Promise<string | null> => {
//   try {
//     const functionsInstance = getFunctions(undefined, 'us-central1'); // ← add region
//     const createPortalSessionFn = httpsCallable(functionsInstance, 'createStripePortalSession');
//     const result: any = await createPortalSessionFn({ userId: uid, returnUrl });
//     return result.data?.url ?? null;
//   } catch (err) {
//     console.error("Error creating portal session:", err);
//     return null;
//   }
// },
createPortalSession: async (uid: string, returnUrl: string): Promise<string | null> => {
  try {
    const headers = await authHeader();
    const res = await fetch(API_BASE.portalSession, {
      method: "POST",
      headers,
      body: JSON.stringify({ userId: uid, returnUrl }),
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const data = await res.json();
    return data.url ?? null;
  } catch (err) {
    console.error("Error creating portal session:", err);
    return null;
  }
},
};
